import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Shield, Brain, Lock, Eye, AlertTriangle, CheckCircle,
  Activity, TrendingUp, DollarSign, FileText, Search,
  Cpu, Globe, Users, BarChart3, Fingerprint, XCircle,
  Zap, Target, Radio, Box, Code, Settings
} from 'lucide-react';

import { authFetch, API_URL } from '../utils/authFetch';

const AISecurityDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [gateway, setGateway] = useState(null);
  const [agents, setAgents] = useState(null);
  const [supplyChain, setSupplyChain] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [promptAnalysis, setPromptAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [ovRes, gwRes, agRes, scRes, coRes, paRes] = await Promise.all([
        authFetch(`${API_URL}/api/securesphere/ai-security/overview`).then(r => r.json()),
        authFetch(`${API_URL}/api/securesphere/ai-security/gateway/dashboard`).then(r => r.json()),
        authFetch(`${API_URL}/api/securesphere/ai-security/agents/dashboard`).then(r => r.json()),
        authFetch(`${API_URL}/api/securesphere/ai-security/supply-chain/dashboard`).then(r => r.json()),
        authFetch(`${API_URL}/api/securesphere/ai-security/compliance/dashboard`).then(r => r.json()),
        authFetch(`${API_URL}/api/securesphere/ai-security/gateway/prompt-analysis`).then(r => r.json())
      ]);
      setOverview(ovRes);
      setGateway(gwRes);
      setAgents(agRes);
      setSupplyChain(scRes);
      setCompliance(coRes);
      setPromptAnalysis(paRes);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const severityColor = useMemo(() => ({
    critical: 'bg-red-500/20 text-red-400 border-red-500/30',
    high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    low: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    info: 'bg-neutral-500/20 text-neutral-400 border-neutral-500/30'
  }), []);

  const pillarStatusColor = useMemo(() => ({
    operational: 'bg-green-500/20 text-green-400',
    needs_attention: 'bg-yellow-500/20 text-yellow-400',
    compliant: 'bg-green-500/20 text-green-400',
    at_risk: 'bg-red-500/20 text-red-400'
  }), []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="ai-security-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-security-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">AI Security Platform</h2>
          <p className="text-neutral-400">Enterprise AI/ML Workload Protection Across All Departments</p>
        </div>
        {overview && (
          <Badge className="bg-gradient-to-r from-orange-500/30 to-amber-500/30 text-orange-300 border-orange-500/40 text-lg px-4 py-1">
            <Shield className="w-4 h-4 mr-2" />
            Score: {overview.overall_score}/100
          </Badge>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="gateway">API Gateway</TabsTrigger>
          <TabsTrigger value="agents">Agentic AI</TabsTrigger>
          <TabsTrigger value="supply-chain">Supply Chain</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
        </TabsList>

        {/* ===== OVERVIEW ===== */}
        <TabsContent value="overview" className="space-y-6">
          {overview && (
            <>
              {/* Four Pillars */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(overview.pillars).map(([key, pillar]) => (
                  <Card key={key} className="bg-[#242424] border-white/10 cursor-pointer hover:border-orange-500/30 transition-colors" onClick={() => setActiveTab(key === 'api_gateway' ? 'gateway' : key === 'agentic_guardrails' ? 'agents' : key === 'supply_chain' ? 'supply-chain' : 'compliance')}>
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <Badge className={pillarStatusColor[pillar.status] || pillarStatusColor.operational}>
                          {pillar.status.replace('_', ' ')}
                        </Badge>
                        <span className="text-2xl font-bold text-white">{pillar.score}</span>
                      </div>
                      <h3 className="text-white font-semibold mb-1 capitalize">{key.replace(/_/g, ' ')}</h3>
                      <p className="text-xs text-neutral-400 mb-2">{pillar.key_metric}</p>
                      <p className="text-xs text-orange-400">{pillar.top_risk}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Enterprise Coverage */}
              <Card className="bg-[#242424] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Users className="w-5 h-5 text-orange-400" />
                    Enterprise AI Coverage by Department
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {Object.entries(overview.enterprise_coverage).map(([dept, data]) => (
                      <div key={dept} className="bg-[#1a1a1a] rounded-lg p-4 text-center">
                        <div className="flex items-center justify-center mb-2">
                          <CheckCircle className="w-5 h-5 text-green-400" />
                        </div>
                        <p className="text-white font-semibold capitalize text-sm">{dept}</p>
                        <Badge className={severityColor[data.risk_level] || severityColor.medium} variant="outline">
                          {data.risk_level}
                        </Badge>
                        <p className="text-xs text-neutral-500 mt-1">{data.ai_workloads} workloads</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Technology Stack */}
              <Card className="bg-gradient-to-br from-orange-500/10 to-amber-500/5 border-orange-500/30">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Cpu className="w-5 h-5 text-orange-400" />
                    {overview.technology_stack.detection_engine}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-sm text-neutral-400 mb-3">Core Capabilities</h4>
                      <div className="space-y-2">
                        {overview.technology_stack.capabilities.slice(0, 5).map((cap, i) => (
                          <div key={cap} className="flex items-start gap-2">
                            <CheckCircle className="w-3.5 h-3.5 text-green-400 mt-0.5 flex-shrink-0" />
                            <span className="text-xs text-neutral-300">{cap}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm text-neutral-400 mb-3">Additional Features</h4>
                      <div className="space-y-2">
                        {overview.technology_stack.capabilities.slice(5).map((cap, i) => (
                          <div key={cap} className="flex items-start gap-2">
                            <CheckCircle className="w-3.5 h-3.5 text-green-400 mt-0.5 flex-shrink-0" />
                            <span className="text-xs text-neutral-300">{cap}</span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4">
                        <h4 className="text-sm text-neutral-400 mb-2">Supported Providers</h4>
                        <div className="flex flex-wrap gap-1.5">
                          {overview.technology_stack.supported_providers.map((p, i) => (
                            <Badge key={p} variant="outline" className="text-orange-400 border-orange-500/30 text-xs">{p}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ===== API GATEWAY ===== */}
        <TabsContent value="gateway" className="space-y-6">
          {gateway && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <Activity className="w-5 h-5 text-blue-400 mb-2" />
                    <p className="text-xs text-neutral-400">API Calls (24h)</p>
                    <p className="text-2xl font-bold text-white">{(gateway.summary.total_api_calls_24h / 1000).toFixed(0)}K</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <XCircle className="w-5 h-5 text-red-400 mb-2" />
                    <p className="text-xs text-neutral-400">Blocked Requests</p>
                    <p className="text-2xl font-bold text-red-400">{gateway.summary.blocked_requests}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <Fingerprint className="w-5 h-5 text-purple-400 mb-2" />
                    <p className="text-xs text-neutral-400">PII Detections</p>
                    <p className="text-2xl font-bold text-purple-400">{gateway.summary.pii_detections}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <Target className="w-5 h-5 text-orange-400 mb-2" />
                    <p className="text-xs text-neutral-400">Injections Blocked</p>
                    <p className="text-2xl font-bold text-orange-400">{gateway.summary.prompt_injections_blocked}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Threat Breakdown */}
              <Card className="bg-[#242424] border-white/10">
                <CardHeader><CardTitle className="text-white">Threat Detection Breakdown</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {Object.entries(gateway.threat_breakdown).map(([threat, data]) => (
                      <div key={threat} className="bg-[#1a1a1a] rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm font-semibold capitalize">{threat.replace(/_/g, ' ')}</p>
                          <Badge className={severityColor[data.severity]}>{data.severity}</Badge>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-white">{data.count}</p>
                          <span className={`text-xs ${data.trend === 'up' ? 'text-red-400' : data.trend === 'down' ? 'text-green-400' : 'text-neutral-400'}`}>
                            {data.trend === 'up' ? 'Trending up' : data.trend === 'down' ? 'Trending down' : 'Stable'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Top Models + Department Usage */}
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-[#242424] border-white/10">
                  <CardHeader><CardTitle className="text-white">Models Monitored</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {gateway.top_models.map((m, i) => (
                        <div key={m.model || m.name || i} className="flex items-center justify-between bg-[#1a1a1a] rounded p-3">
                          <div>
                            <p className="text-white font-semibold text-sm">{m.model}</p>
                            <p className="text-xs text-neutral-500">{m.provider} | {m.calls_24h.toLocaleString()} calls</p>
                          </div>
                          <Badge className={m.risk_score > 40 ? 'bg-red-500/20 text-red-400' : m.risk_score > 20 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-green-500/20 text-green-400'}>
                            Risk: {m.risk_score}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-[#242424] border-white/10">
                  <CardHeader><CardTitle className="text-white">Department AI Usage</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(gateway.department_usage).map(([dept, data]) => (
                        <div key={dept} className="flex items-center justify-between bg-[#1a1a1a] rounded p-3">
                          <div>
                            <p className="text-white font-semibold text-sm capitalize">{dept}</p>
                            <p className="text-xs text-neutral-500">{data.calls.toLocaleString()} calls | {data.incidents} incidents</p>
                          </div>
                          <span className="text-green-400 font-bold text-sm">${(data.cost / 1000).toFixed(1)}K</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Prompt Analysis */}
              {promptAnalysis && (
                <Card className="bg-[#242424] border-white/10">
                  <CardHeader><CardTitle className="text-white">Prompt Injection Detection Engine</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      {promptAnalysis.techniques_detected.map((tech, i) => (
                        <div key={tech} className="bg-[#1a1a1a] rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-white font-semibold text-sm">{tech.technique}</p>
                            <Badge variant="outline" className="text-green-400 border-green-500/30 text-xs">{tech.accuracy}%</Badge>
                          </div>
                          <p className="text-xs text-neutral-400">{tech.description}</p>
                          <p className="text-xs text-orange-400 mt-1">{tech.detections_30d} detections (30d)</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recent Incidents */}
              <Card className="bg-[#242424] border-white/10">
                <CardHeader><CardTitle className="text-white">Recent Security Incidents</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {gateway.recent_incidents.map((inc, i) => (
                      <div key={`inc-${i}`} className="bg-[#1a1a1a] rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge className={severityColor[inc.severity]}>{inc.severity}</Badge>
                            <span className="text-white font-semibold text-sm capitalize">{inc.type.replace(/_/g, ' ')}</span>
                          </div>
                          <Badge className={inc.blocked ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                            {inc.blocked ? 'Blocked' : 'Detected'}
                          </Badge>
                        </div>
                        <p className="text-sm text-neutral-300">{inc.description}</p>
                        <p className="text-xs text-neutral-500 mt-1">Model: {inc.model} | Dept: {inc.department}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ===== AGENTIC AI ===== */}
        <TabsContent value="agents" className="space-y-6">
          {agents && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <Brain className="w-5 h-5 text-purple-400 mb-2" />
                    <p className="text-xs text-neutral-400">Registered Agents</p>
                    <p className="text-2xl font-bold text-white">{agents.summary.registered_agents}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <Activity className="w-5 h-5 text-green-400 mb-2" />
                    <p className="text-xs text-neutral-400">Actions Today</p>
                    <p className="text-2xl font-bold text-white">{agents.summary.actions_today.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <XCircle className="w-5 h-5 text-red-400 mb-2" />
                    <p className="text-xs text-neutral-400">Blocked Actions</p>
                    <p className="text-2xl font-bold text-red-400">{agents.summary.blocked_actions}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Agent Types */}
              <Card className="bg-[#242424] border-white/10">
                <CardHeader><CardTitle className="text-white">AI Agent Inventory</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {agents.agent_types.map((agent, i) => (
                      <div key={`agent-${i}`} className="bg-[#1a1a1a] rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-white font-semibold">{agent.type}</p>
                          <Badge className={severityColor[agent.risk_level]}>{agent.risk_level}</Badge>
                        </div>
                        <p className="text-xs text-neutral-500">{agent.count} agents | {agent.department}</p>
                        <p className="text-xs text-neutral-400">{agent.actions_24h.toLocaleString()} actions (24h)</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Tool Usage */}
              <Card className="bg-[#242424] border-white/10">
                <CardHeader><CardTitle className="text-white">Tool Access Control</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(agents.tool_usage).map(([tool, data]) => (
                      <div key={tool} className="bg-[#1a1a1a] rounded p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white font-semibold text-sm capitalize">{tool.replace(/_/g, ' ')}</span>
                          <div className="flex gap-3 text-xs">
                            <span className="text-green-400">{data.allowed.toLocaleString()} allowed</span>
                            <span className="text-red-400">{data.blocked} blocked</span>
                            <span className="text-yellow-400">{data.audited} audited</span>
                          </div>
                        </div>
                        <div className="h-2 bg-black/30 rounded-full overflow-hidden">
                          <div className="h-full flex">
                            <div className="bg-green-500 h-full" style={{ width: `${(data.allowed / (data.allowed + data.blocked + data.audited)) * 100}%` }} />
                            <div className="bg-yellow-500 h-full" style={{ width: `${(data.audited / (data.allowed + data.blocked + data.audited)) * 100}%` }} />
                            <div className="bg-red-500 h-full" style={{ width: `${(data.blocked / (data.allowed + data.blocked + data.audited)) * 100}%` }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Guardrail Events */}
              <Card className="bg-[#242424] border-white/10">
                <CardHeader><CardTitle className="text-white">Recent Guardrail Events</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {agents.guardrail_events.map((ev, i) => (
                      <div key={`ev-${i}`} className="bg-[#1a1a1a] rounded-lg p-3 flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={severityColor[ev.severity]}>{ev.severity}</Badge>
                            <span className="text-white text-sm font-semibold">{ev.agent}</span>
                          </div>
                          <p className="text-sm text-neutral-300">{ev.reason}</p>
                          <p className="text-xs text-neutral-500">Action: {ev.action}</p>
                        </div>
                        <Badge className={ev.status === 'blocked' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}>
                          {ev.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ===== SUPPLY CHAIN ===== */}
        <TabsContent value="supply-chain" className="space-y-6">
          {supplyChain && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <Search className="w-5 h-5 text-blue-400 mb-2" />
                    <p className="text-xs text-neutral-400">Models Scanned</p>
                    <p className="text-2xl font-bold text-white">{supplyChain.summary.models_scanned}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <AlertTriangle className="w-5 h-5 text-orange-400 mb-2" />
                    <p className="text-xs text-neutral-400">Vulnerabilities</p>
                    <p className="text-2xl font-bold text-orange-400">{supplyChain.summary.vulnerabilities_found}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <XCircle className="w-5 h-5 text-red-400 mb-2" />
                    <p className="text-xs text-neutral-400">Poisoned Models</p>
                    <p className="text-2xl font-bold text-red-400">{supplyChain.summary.poisoned_models_detected}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Model Inventory */}
              <Card className="bg-[#242424] border-white/10">
                <CardHeader><CardTitle className="text-white">Model Inventory & Integrity</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {supplyChain.model_inventory.map((model, i) => (
                      <div key={`model-${i}`} className="bg-[#1a1a1a] rounded-lg p-4 flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-white font-semibold">{model.name}</p>
                            <Badge variant="outline" className="text-neutral-400 border-white/20 text-xs">{model.provider}</Badge>
                            <Badge variant="outline" className="text-neutral-400 border-white/20 text-xs">v{model.version}</Badge>
                          </div>
                          <p className="text-xs text-neutral-500">{model.dependencies} deps | {model.known_vulns} vulns</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={
                            model.integrity === 'verified' ? 'bg-green-500/20 text-green-400' :
                            model.integrity === 'warning' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }>
                            {model.integrity.replace('_', ' ')}
                          </Badge>
                          <span className={`text-sm font-bold ${model.risk_score > 40 ? 'text-red-400' : model.risk_score > 20 ? 'text-yellow-400' : 'text-green-400'}`}>
                            {model.risk_score}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Vulnerability Categories */}
              <Card className="bg-[#242424] border-white/10">
                <CardHeader><CardTitle className="text-white">Vulnerability Categories</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-3">
                    {supplyChain.vulnerability_categories.map((cat, i) => (
                      <div key={`cat-${i}`} className="bg-[#1a1a1a] rounded-lg p-3 flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-white font-semibold text-sm">{cat.category}</p>
                            <Badge className={severityColor[cat.severity]}>{cat.severity}</Badge>
                          </div>
                          <p className="text-xs text-neutral-400">{cat.description}</p>
                        </div>
                        <span className="text-white font-bold text-lg">{cat.count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* SBOM */}
              <Card className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-blue-500/30">
                <CardContent className="p-5">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <Box className="w-5 h-5 text-blue-400" /> AI Software Bill of Materials (SBOM)
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-white">{supplyChain.sbom.total_components}</p>
                      <p className="text-xs text-neutral-400">Total Components</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-white">{supplyChain.sbom.direct_dependencies}</p>
                      <p className="text-xs text-neutral-400">Direct Deps</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-orange-400">{supplyChain.sbom.known_vulnerabilities}</p>
                      <p className="text-xs text-neutral-400">Known Vulns</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-white">{supplyChain.sbom.transitive_dependencies}</p>
                      <p className="text-xs text-neutral-400">Transitive Deps</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ===== COMPLIANCE ===== */}
        <TabsContent value="compliance" className="space-y-6">
          {compliance && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/10 border-green-500/30">
                  <CardContent className="p-4">
                    <Shield className="w-5 h-5 text-green-400 mb-2" />
                    <p className="text-xs text-neutral-400">Compliance Score</p>
                    <p className="text-2xl font-bold text-white">{compliance.summary.compliance_score}%</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <DollarSign className="w-5 h-5 text-green-400 mb-2" />
                    <p className="text-xs text-neutral-400">Monthly AI Spend</p>
                    <p className="text-2xl font-bold text-white">${(compliance.summary.total_ai_spend_monthly / 1000).toFixed(0)}K</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <FileText className="w-5 h-5 text-blue-400 mb-2" />
                    <p className="text-xs text-neutral-400">Audit Events (30d)</p>
                    <p className="text-2xl font-bold text-white">{(compliance.summary.audit_events_30d / 1000).toFixed(0)}K</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <AlertTriangle className="w-5 h-5 text-orange-400 mb-2" />
                    <p className="text-xs text-neutral-400">Active Violations</p>
                    <p className="text-2xl font-bold text-orange-400">{compliance.summary.active_violations}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Regulatory Compliance */}
              <Card className="bg-[#242424] border-white/10">
                <CardHeader><CardTitle className="text-white">Regulatory Compliance Status</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {compliance.regulatory_compliance.map((reg, i) => (
                      <div key={`reg-${i}`} className="bg-[#1a1a1a] rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-white font-semibold text-sm">{reg.framework}</p>
                          <Badge className={reg.status === 'compliant' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                            {reg.status.replace('_', ' ')}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex-1 h-2 bg-black/30 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${reg.score >= 85 ? 'bg-green-500' : reg.score >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${reg.score}%` }} />
                          </div>
                          <span className="text-white font-bold text-sm">{reg.score}%</span>
                        </div>
                        <p className="text-xs text-neutral-500">Next audit: {reg.next_audit}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Cost by Department */}
              <Card className="bg-[#242424] border-white/10">
                <CardHeader><CardTitle className="text-white">AI Spend by Department</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(compliance.cost_by_department).map(([dept, data]) => {
                      const overBudget = data.utilization > 100;
                      return (
                        <div key={dept} className="bg-[#1a1a1a] rounded p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white font-semibold capitalize">{dept}</span>
                            <div className="flex items-center gap-3 text-sm">
                              <span className="text-neutral-400">${(data.monthly / 1000).toFixed(1)}K / ${(data.budget / 1000).toFixed(1)}K</span>
                              <Badge className={overBudget ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}>
                                {data.utilization.toFixed(0)}%
                              </Badge>
                            </div>
                          </div>
                          <div className="h-2 bg-black/30 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${overBudget ? 'bg-red-500' : data.utilization > 80 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{ width: `${Math.min(data.utilization, 100)}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Usage Policies */}
              <Card className="bg-[#242424] border-white/10">
                <CardHeader><CardTitle className="text-white">AI Usage Policies</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {compliance.usage_policies.map((pol, i) => (
                      <div key={`pol-${i}`} className="bg-[#1a1a1a] rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <p className="text-white font-semibold text-sm">{pol.name}</p>
                          <p className="text-xs text-neutral-500">{pol.type.replace(/_/g, ' ')} | {pol.department}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-orange-400 text-sm font-bold">{pol.violations_30d} violations</span>
                          <Badge className={pol.status === 'enforcing' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'}>
                            {pol.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AISecurityDashboard;
